/**
 * Beschaffung: eingehende Angebote, Bestellungen und Ersatzteillager.
 *
 * Drei Dinge, die zusammengehören, weil sie dieselbe Frage stellen: *Wofür gebe
 * ich jetzt Geld aus?* Links kommt herein, was ungefragt angeboten wird, rechts
 * steht, was bereits unterwegs ist und was im Regal liegt.
 *
 * Bei den Angeboten steht die Bewertung gegenüber dem Listenpreis bewusst
 * direkt neben dem Preis. Wer will, kann trotzdem zusagen – aber niemand kann
 * sagen, er habe es nicht gesehen.
 */
import { useEffect, useMemo, useState } from 'react';
import type { MarketOffer, PartModel, PartOrder } from '@hosting-sim/core';
import { PART_KIND_LABEL, findPart, judgeOffer } from '@hosting-sim/core';
import {
  Badge,
  Button,
  Card,
  Column,
  DataTable,
  EmptyState,
  Field,
  Input,
  Modal,
  Mono,
  Notice,
  PageHeader,
  Select,
  Stat,
  StatGrid,
  type Tone,
} from '../components/ui';
import { useGame, useMetrics } from '../store/game';
import { api } from '../lib/api';
import { money, num } from '../lib/format';

const VERDICT: Record<string, { label: string; tone: Tone }> = {
  schnaeppchen: { label: 'deutlich unter Markt', tone: 'success' },
  marktueblich: { label: 'marktüblich', tone: 'info' },
  teuer: { label: 'über Markt', tone: 'warning' },
  wucher: { label: 'weit über Markt', tone: 'danger' },
};

const OFFER_KIND: Record<MarketOffer['kind'], string> = {
  transit: 'Transit',
  'ip-block': 'IPv4-Block',
  colocation: 'Standort',
  peering: 'Peering',
  fiber: 'Glasfaser',
  hardware: 'Hardware',
};

// ---------------------------------------------------------------------------
// Eingehende Angebote
// ---------------------------------------------------------------------------

export function Offers() {
  const world = useGame((s) => s.world);
  const live = useGame((s) => s.live);
  const run = useGame((s) => s.run);
  const [chosen, setChosen] = useState<MarketOffer | null>(null);

  // Live-Angebote haben Vorrang: Sie laufen ab, der geladene Weltzustand ist älter.
  const offers = live?.offers ?? world?.market.offers ?? [];
  const tick = live?.meta.tick ?? world?.meta.tick ?? 0;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Eingehende Angebote"
        description="Was Anbieter von sich aus anbieten. Manches lohnt sich, das meiste nicht."
      />

      {offers.length === 0 ? (
        <EmptyState
          title="Gerade ruft niemand an"
          description="Je bekannter dein Netz wird, desto häufiger melden sich Carrier, Broker und Händler von selbst."
        />
      ) : (
        <div className="grid gap-3 lg:grid-cols-2">
          {offers.map((offer) => (
            <OfferCard key={offer.id} offer={offer} tick={tick} onAccept={() => setChosen(offer)} onDecline={() => run({ type: 'market/declineOffer', offerId: offer.id })} />
          ))}
        </div>
      )}

      {chosen && <AcceptOfferDialog offer={chosen} onClose={() => setChosen(null)} />}
    </div>
  );
}

function OfferCard({
  offer,
  tick,
  onAccept,
  onDecline,
}: {
  offer: MarketOffer;
  tick: number;
  onAccept: () => void;
  onDecline: () => void;
}) {
  const ratio = typeof offer.payload.priceRatio === 'number' ? offer.payload.priceRatio : 1;
  const verdict = VERDICT[(offer.payload.verdict as string) ?? judgeOffer(ratio)] ?? VERDICT.marktueblich;
  const hoursLeft = Math.max(0, offer.expiresAtTick - tick);

  return (
    <Card
      title={offer.title}
      subtitle={`${offer.sellerName} · ${OFFER_KIND[offer.kind]}`}
      actions={<Badge tone={verdict.tone}>{verdict.label}</Badge>}
    >
      <p className="text-xs leading-relaxed text-panel-600 dark:text-panel-300">{offer.description}</p>

      <div className="mt-3 grid grid-cols-3 gap-3">
        <Field label="Einmalig">{offer.setupPrice > 0 ? money(offer.setupPrice) : '—'}</Field>
        <Field label="Monatlich">{offer.monthlyPrice > 0 ? money(offer.monthlyPrice) : '—'}</Field>
        <Field label="Preis zum Markt">
          <span className={ratio > 1.05 ? 'text-amber-600 dark:text-amber-400' : ratio < 0.95 ? 'text-accent-600 dark:text-accent-400' : ''}>
            {ratio > 1 ? '+' : ''}
            {((ratio - 1) * 100).toFixed(0)} %
          </span>
        </Field>
      </div>

      <div className="mt-3 flex items-center justify-between gap-2">
        <span className="text-xs text-panel-500 dark:text-panel-400">
          {hoursLeft > 24 ? `noch ${Math.round(hoursLeft / 24)} Tage` : `noch ${hoursLeft} h`}
        </span>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={onDecline}>
            Ablehnen
          </Button>
          <Button variant="primary" onClick={onAccept}>
            Annehmen
          </Button>
        </div>
      </div>
    </Card>
  );
}

/**
 * Für Transit, Peering und Hardware fehlt noch, wo das Ganze hin soll.
 * Angebote ohne solche Wahl werden direkt angenommen.
 */
function AcceptOfferDialog({ offer, onClose }: { offer: MarketOffer; onClose: () => void }) {
  const world = useGame((s) => s.world);
  const run = useGame((s) => s.run);
  const [routerId, setRouterId] = useState('');
  const [portIndex, setPortIndex] = useState('');
  const [rackId, setRackId] = useState('');
  const [busy, setBusy] = useState(false);

  const needsPort = offer.payload.needsPort === true;
  const needsRack = offer.payload.needsRack === true;

  const routers = Object.values(world?.routers ?? {});
  const selectedRouter = routers.find((r) => r.id === routerId);
  const freePorts = selectedRouter?.ports.filter((p) => p.linkId === null) ?? [];
  const racks = Object.values(world?.racks ?? {});

  useEffect(() => {
    if (needsPort && !routerId && routers[0]) setRouterId(routers[0].id);
    if (needsRack && !rackId && racks[0]) setRackId(racks[0].id);
  }, [needsPort, needsRack, routerId, rackId, routers, racks]);

  useEffect(() => {
    if (freePorts.length > 0 && !freePorts.some((p) => String(p.index) === portIndex)) {
      setPortIndex(String(freePorts[0].index));
    }
  }, [freePorts, portIndex]);

  async function accept() {
    setBusy(true);
    try {
      const result = await run({
        type: 'market/acceptOffer',
        offerId: offer.id,
        ...(needsPort ? { routerId: routerId as never, portIndex: Number(portIndex) } : {}),
        ...(needsRack ? { rackId: rackId as never } : {}),
      });
      if (result.ok) onClose();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open onClose={onClose} title={`Angebot annehmen: ${offer.title}`}>
      <div className="space-y-3">
        <Notice tone="info" title={offer.sellerName}>
          {offer.description}
        </Notice>

        {needsPort && (
          <>
            <Select label="Router" value={routerId} onChange={(e) => setRouterId(e.target.value)}>
              {routers.map((router) => (
                <option key={router.id} value={router.id}>
                  {router.name} ({router.ports.filter((p) => p.linkId === null).length} freie Ports)
                </option>
              ))}
            </Select>
            <Select label="Port" value={portIndex} onChange={(e) => setPortIndex(e.target.value)}>
              {freePorts.map((port) => (
                <option key={port.index} value={port.index}>
                  Port {port.index} · {port.speedMbps >= 1000 ? `${port.speedMbps / 1000} Gbit/s` : `${port.speedMbps} Mbit/s`}
                </option>
              ))}
            </Select>
            {freePorts.length === 0 && <Notice tone="warning">Dieser Router hat keinen freien Port.</Notice>}
          </>
        )}

        {needsRack && (
          <Select label="Rack" value={rackId} onChange={(e) => setRackId(e.target.value)}>
            {racks.map((rack) => (
              <option key={rack.id} value={rack.id}>
                {rack.name}
              </option>
            ))}
          </Select>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button variant="primary" loading={busy} onClick={accept} disabled={needsPort && freePorts.length === 0}>
            Verbindlich annehmen
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Ersatzteile: Bestellungen und Lager
// ---------------------------------------------------------------------------

export function Parts() {
  const world = useGame((s) => s.world);
  const run = useGame((s) => s.run);
  const [ordering, setOrdering] = useState(false);

  const datacenters = Object.values(world?.datacenters ?? {});
  const orders = Object.values(world?.partOrders ?? {}).filter((o) => o.state === 'ordered');
  const tick = world?.meta.tick ?? 0;

  const stock = useMemo(() => {
    const rows: { datacenterId: string; datacenterName: string; modelKey: string; part: PartModel | undefined; quantity: number }[] = [];
    for (const [datacenterId, shelf] of Object.entries(world?.inventory ?? {})) {
      for (const [modelKey, quantity] of Object.entries(shelf)) {
        if (quantity <= 0) continue;
        rows.push({
          datacenterId,
          datacenterName: world?.datacenters[datacenterId as never]?.name ?? datacenterId,
          modelKey,
          part: findPart(modelKey),
          quantity,
        });
      }
    }
    return rows.sort((a, b) => a.datacenterName.localeCompare(b.datacenterName) || (a.part?.name ?? '').localeCompare(b.part?.name ?? ''));
  }, [world]);

  const stockValue = stock.reduce((sum, row) => sum + (row.part?.price ?? 0) * row.quantity, 0);
  const openValue = orders.reduce((sum, order) => sum + order.unitPrice * order.quantity, 0);

  const orderColumns: Column<PartOrder>[] = [
    { key: 'part', header: 'Bauteil', render: (o) => findPart(o.modelKey)?.name ?? o.modelKey },
    { key: 'qty', header: 'Stück', align: 'right', render: (o) => num(o.quantity) },
    { key: 'dc', header: 'Lieferziel', render: (o) => world?.datacenters[o.datacenterId]?.name ?? o.datacenterId },
    {
      key: 'eta',
      header: 'Ankunft',
      render: (o) => {
        const left = Math.max(0, o.arrivesAtTick - tick);
        return (
          <span>
            in {left} h {o.express && <Badge tone="info">Express</Badge>}
          </span>
        );
      },
    },
    { key: 'sum', header: 'Wert', align: 'right', render: (o) => money(o.unitPrice * o.quantity) },
    {
      key: 'actions',
      header: '',
      render: (o) => (
        <Button size="sm" variant="ghost" onClick={() => run({ type: 'hw/cancelPartOrder', orderId: o.id })}>
          Stornieren
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <PageHeader
        title="Ersatzteile"
        description="Bauteile bestellen, Lieferungen verfolgen, Lagerbestand verwalten."
        actions={
          <Button variant="primary" onClick={() => setOrdering(true)} disabled={datacenters.length === 0}>
            Bauteile bestellen
          </Button>
        }
      />

      <StatGrid cols={3}>
        <Stat label="Lagerwert" value={money(stockValue)} hint={`${stock.reduce((s, r) => s + r.quantity, 0)} Bauteile`} />
        <Stat label="Unterwegs" value={money(openValue)} hint={`${orders.length} offene Bestellungen`} />
        <Stat label="Standorte mit Lager" value={new Set(stock.map((s) => s.datacenterId)).size} />
      </StatGrid>

      <Card title="Offene Bestellungen" subtitle="Bezahlt ist bei Bestellung; eine Stornierung erstattet den Betrag.">
        {orders.length === 0 ? (
          <EmptyState title="Nichts unterwegs" description="Bestellte Bauteile erscheinen hier mit ihrer Lieferzeit." />
        ) : (
          <DataTable columns={orderColumns} rows={orders} rowKey={(o) => o.id} />
        )}
      </Card>

      <Card title="Lagerbestand" subtitle="Ersatzteile liegen am Standort und lassen sich nur dort verbauen.">
        {stock.length === 0 ? (
          <EmptyState title="Leeres Lager" description="Ohne Vorrat dauert jede Aufrüstung so lange wie die Lieferzeit." />
        ) : (
          <div className="space-y-4">
            {[...new Set(stock.map((s) => s.datacenterId))].map((dcId) => (
              <div key={dcId}>
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-panel-500 dark:text-panel-400">
                  {world?.datacenters[dcId as never]?.name ?? dcId}
                </h3>
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {stock
                    .filter((s) => s.datacenterId === dcId)
                    .map((row) => (
                      <div key={row.modelKey} className="flex items-center justify-between gap-2 rounded-lg border divider p-2">
                        <div className="min-w-0">
                          <p className="truncate text-sm">{row.part?.name ?? row.modelKey}</p>
                          <p className="text-xs text-panel-500 dark:text-panel-400">
                            {row.part ? PART_KIND_LABEL[row.part.kind] : '—'} · {money(row.part?.price ?? 0)}
                          </p>
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          <Badge>{row.quantity}×</Badge>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              run({ type: 'hw/sellParts', datacenterId: dcId as never, modelKey: row.modelKey, quantity: row.quantity })
                            }
                            title="Zum Restwert verkaufen"
                          >
                            Verkaufen
                          </Button>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {ordering && <OrderPartsDialog onClose={() => setOrdering(false)} />}
    </div>
  );
}

function OrderPartsDialog({ onClose }: { onClose: () => void }) {
  const world = useGame((s) => s.world);
  const gameId = useGame((s) => s.gameId);
  const run = useGame((s) => s.run);

  const [catalogue, setCatalogue] = useState<Partial<Record<string, PartModel[]>> | null>(null);
  const [datacenterId, setDatacenterId] = useState(Object.keys(world?.datacenters ?? {})[0] ?? '');
  const [modelKey, setModelKey] = useState('');
  const [quantity, setQuantity] = useState('4');
  const [express, setExpress] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!gameId) return;
    void api.catalog(gameId).then((c) => {
      setCatalogue(c.parts);
      const first = c.parts?.ram?.[0];
      if (first) setModelKey(first.key);
    });
  }, [gameId]);

  const all = useMemo<PartModel[]>(
    () => (catalogue ? Object.values(catalogue).flatMap((list) => list ?? []) : []),
    [catalogue],
  );
  const selected = all.find((p) => p.key === modelKey);
  const count = Math.max(1, Number(quantity) || 1);
  const unitPrice = selected ? Math.round(selected.price * (express ? 1.45 : 1)) : 0;
  const leadTime = selected ? Math.max(1, Math.round(selected.leadTimeTicks / (express ? 4 : 1))) : 0;

  return (
    <Modal open onClose={onClose} title="Bauteile bestellen">
      <div className="space-y-3">
        <Select label="Lieferziel" value={datacenterId} onChange={(e) => setDatacenterId(e.target.value)}>
          {Object.values(world?.datacenters ?? {}).map((dc) => (
            <option key={dc.id} value={dc.id}>
              {dc.name} ({dc.city})
            </option>
          ))}
        </Select>

        <Select label="Bauteil" value={modelKey} onChange={(e) => setModelKey(e.target.value)}>
          {catalogue &&
            Object.entries(catalogue).map(([kind, parts]) => (
              <optgroup key={kind} label={PART_KIND_LABEL[kind as never] ?? kind}>
                {(parts ?? []).map((part) => (
                  <option key={part.key} value={part.key}>
                    {part.name} — {money(part.price)}
                  </option>
                ))}
              </optgroup>
            ))}
        </Select>

        {selected && (
          <Notice tone="info" title={selected.name}>
            {selected.description}
          </Notice>
        )}

        <Input label="Stückzahl" type="number" min={1} value={quantity} onChange={(e) => setQuantity(e.target.value)} />

        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={express} onChange={(e) => setExpress(e.target.checked)} />
          Expresslieferung (45 % Aufschlag, ein Viertel der Lieferzeit)
        </label>

        {selected && (
          <div className="grid grid-cols-3 gap-3 rounded-lg border divider p-3">
            <Field label="Stückpreis">{money(unitPrice)}</Field>
            <Field label="Gesamt">{money(unitPrice * count)}</Field>
            <Field label="Lieferzeit">{leadTime} h</Field>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={!selected || !datacenterId}
            onClick={async () => {
              setBusy(true);
              try {
                const result = await run({
                  type: 'hw/orderParts',
                  datacenterId: datacenterId as never,
                  modelKey,
                  quantity: count,
                  express,
                });
                if (result.ok) onClose();
              } finally {
                setBusy(false);
              }
            }}
          >
            Bestellen
          </Button>
        </div>
      </div>
    </Modal>
  );
}
